import { Shield } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import type { CompetencyDeclaration } from '@/types';
import {
  DECLARATION_TYPE_BADGES,
  DECLARATION_TYPE_LABELS,
  DECLARATION_STATUS_BADGES,
  DECLARATION_STATUS_LABELS,
} from '../_labels';

export function DeclarationsSection({ declarations }: { declarations?: CompetencyDeclaration[] }) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Mis Declaraciones de Competencia</h2>
      </div>
      {!declarations?.length ? (
        <EmptyState
          title="Sin declaraciones"
          description="No tienes declaraciones de competencia registradas."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {declarations.map((decl) => (
            <div key={decl.id} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-card-foreground">
                    {decl.position?.name ?? 'Sin posición'}
                  </h3>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        DECLARATION_TYPE_BADGES[decl.declaration_type],
                      )}
                    >
                      {DECLARATION_TYPE_LABELS[decl.declaration_type] ?? decl.declaration_type}
                    </span>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        DECLARATION_STATUS_BADGES[decl.status],
                      )}
                    >
                      {DECLARATION_STATUS_LABELS[decl.status]}
                    </span>
                  </div>
                  {decl.scope === 'competency_group' && decl.competencies?.length ? (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Competencias:{' '}
                      {decl.competencies.map((c) => c.position_competency.name).join(', ')}
                    </p>
                  ) : decl.scope === 'position' ? (
                    <p className="mt-2 text-xs text-muted-foreground">Puesto completo</p>
                  ) : null}
                  <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
                    {decl.issued_at && <p>Emitida: {formatDate(decl.issued_at)}</p>}
                    {decl.valid_until && <p>Vence: {formatDate(decl.valid_until)}</p>}
                  </div>
                  {decl.file_url && (
                    <a
                      href={decl.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-xs font-medium text-primary hover:underline"
                    >
                      Descargar PDF
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
